import { useState, useEffect, useContext } from "react";
import axios from "axios";
import { Mail, X, ChevronRight, Clock } from "lucide-react";
import { toast } from "sonner";
import { API, AuthContext } from "../App";

const OfflineMessagesPopup = ({ onClose, onOpenPM }) => {
  const { token, user } = useContext(AuthContext);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOfflineMessages = async () => {
      try {
        const response = await axios.get(`${API}/user/offline-messages`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMessages((response.data.messages || []).slice().sort(
          (a, b) => new Date(b.timestamp || b.created_at || 0) - new Date(a.timestamp || a.created_at || 0)
        ));
      } catch (error) {
        console.error("Failed to fetch offline messages:", error);
      } finally {
        setLoading(false);
      }
    };
    
    if (token) fetchOfflineMessages();
  }, [token]);

  // Acknowledge (mark notified) then close, so they don't reappear next login.
  const ackAndClose = async () => {
    try { await axios.post(`${API}/user/offline-messages/ack`, {}, { headers: { Authorization: `Bearer ${token}` } }); } catch (e) { /* ignore */ }
    onClose?.();
  };

  if (loading) return null;
  if (messages.length === 0) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9998] p-4" data-testid="offline-messages-popup">
      <div className="bg-[#C0C0C0] rounded-lg shadow-2xl w-full max-w-md overflow-hidden border-4 border-[#808080]">
        {/* Title Bar */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            <span className="font-bold">📬 Offline Messages</span>
          </div>
          <button onClick={ackAndClose} className="hover:bg-white/20 p-1 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          <p className="text-sm text-gray-600 mb-3">
            You received {messages.length} message{messages.length > 1 ? 's' : ''} while you were offline:
          </p>
          
          <div className="space-y-2 max-h-80 overflow-y-auto retro-scrollbar">
            {messages.map((msg, index) => (
              <div 
                key={msg.message_id || index}
                className="bg-white rounded-lg p-3 border border-gray-300 hover:border-purple-400 cursor-pointer transition-colors"
                title="Double-click to open this conversation"
                data-testid={`offline-message-${msg.sender_id}`}
                onDoubleClick={() => {
                  onOpenPM?.(msg.sender_id, msg.sender_username);
                  ackAndClose();
                }}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-sm text-purple-600">
                        {msg.sender_username}
                      </span>
                      <span className="text-[10px] text-gray-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(msg.timestamp || msg.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 mt-1 line-clamp-2">
                      {msg.content}
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="px-4 pb-4 flex gap-2">
          <button
            onClick={ackAndClose}
            data-testid="offline-messages-dismiss-btn"
            className="flex-1 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-sm font-medium"
          >
            Dismiss
          </button>
          <button
            onClick={() => {
              if (messages.length > 0) {
                onOpenPM?.(messages[0].sender_id, messages[0].sender_username);
              }
              ackAndClose();
            }}
            className="flex-1 py-2 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded hover:from-purple-400 hover:to-indigo-500 text-sm font-medium"
          >
            Open Messages
          </button>
        </div>
      </div>
    </div>
  );
};

export default OfflineMessagesPopup;
